import * as React from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import { useAddDnsRecord, useUpdateDnsRecord } from "@/hooks/useDns";
import { useToast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";
import type { DnsRecordDto, DnsRecordType, UpsertDnsRecordRequest } from "@/types/api";

interface Props {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  containerId: number;
  domainName: string;
  initial?: DnsRecordDto;
}

const TYPES: DnsRecordType[] = ["A", "AAAA", "CNAME", "MX", "TXT", "NS", "SRV", "CAA"];

/**
 * Used for both Add and Edit. When `initial` is supplied, the dialog runs
 * in edit mode (the type field stays editable in case the user wants to
 * convert A → AAAA etc.; 20i happily replaces the record).
 */
export function DnsRecordDialog({ open, onOpenChange, containerId, domainName, initial }: Props) {
  const isEdit = Boolean(initial);
  const [form, setForm] = React.useState<UpsertDnsRecordRequest>({
    type: "A", name: "@", value: "", ttl: 3600
  });

  const add    = useAddDnsRecord(containerId);
  const update = useUpdateDnsRecord(containerId);
  const { toast } = useToast();

  React.useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        type: initial.type,
        name: initial.name,
        value: initial.value,
        ttl: initial.ttl,
        priority: initial.priority ?? undefined
      });
    } else {
      setForm({ type: "A", name: "@", value: "", ttl: 3600 });
    }
  }, [open, initial]);

  const needsPriority = form.type === "MX" || form.type === "SRV";

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.value.trim()) return;
    try {
      if (isEdit) {
        await update.mutateAsync({ id: initial!.id, body: form });
      } else {
        await add.mutateAsync(form);
      }
      toast({ title: isEdit ? "Record updated" : "Record added", variant: "success" });
      onOpenChange(false);
    } catch (err) {
      const description =
        err instanceof ApiError
          ? `${err.message}${err.code ? ` (${err.code})` : ""}`
          : "Network error.";
      toast({ title: isEdit ? "Update failed" : "Add failed", description, variant: "destructive" });
    }
  }

  const busy = add.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit DNS record" : "Add DNS record"}</DialogTitle>
          <DialogDescription>
            For <span className="font-mono">{domainName}</span>. Use <span className="font-mono">@</span> for the apex.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="type">Type</Label>
              <select
                id="type"
                value={form.type}
                onChange={e => setForm(f => ({ ...f, type: e.target.value as DnsRecordType }))}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                required
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="@ for apex, www, mail..."
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="value">Value</Label>
            <Input
              id="value"
              required
              value={form.value}
              onChange={e => setForm(f => ({ ...f, value: e.target.value }))}
              placeholder={placeholderFor(form.type)}
            />
            <p className="text-xs text-muted-foreground">{hintFor(form.type)}</p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="ttl">TTL (seconds)</Label>
              <Input
                id="ttl"
                type="number"
                inputMode="numeric"
                min={60}
                step={60}
                value={form.ttl ?? 3600}
                onChange={e => setForm(f => ({ ...f, ttl: Number(e.target.value) || 3600 }))}
              />
            </div>
            {needsPriority && (
              <div className="space-y-1.5">
                <Label htmlFor="priority">Priority</Label>
                <Input
                  id="priority"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  value={form.priority ?? 10}
                  onChange={e => setForm(f => ({ ...f, priority: Number(e.target.value) || 0 }))}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy || !form.value.trim()}>
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {isEdit ? "Save changes" : "Add record"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function placeholderFor(t: DnsRecordType): string {
  switch (t) {
    case "A":     return "192.0.2.1";
    case "AAAA":  return "2001:db8::1";
    case "CNAME": return "target.example.com";
    case "MX":    return "mail.example.com";
    case "TXT":   return "v=spf1 include:_spf.20i.com ~all";
    case "NS":    return "ns1.example.com";
    case "SRV":   return "0 5060 sip.example.com";
    case "CAA":   return "0 issue \"letsencrypt.org\"";
  }
}

function hintFor(t: DnsRecordType): string {
  switch (t) {
    case "MX":  return "Target mail host. Set priority too (lower = preferred).";
    case "SRV": return "Format: weight port target.";
    case "TXT": return "Quoted strings allowed.";
    case "CAA": return "Format: flags tag value.";
    default:    return "";
  }
}
